import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';

// ── Animated counter hook ─────────────────────────────────────────────────────
function useCountUp(target, duration = 1800, start = false) {
    const [val, setVal] = useState(0);
    useEffect(() => {
        if (!start || !target) return;
        let startTime = null;
        const step = (ts) => {
            if (!startTime) startTime = ts;
            const progress = Math.min((ts - startTime) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
            setVal(Math.floor(eased * target));
            if (progress < 1) requestAnimationFrame(step);
            else setVal(target);
        };
        requestAnimationFrame(step);
    }, [target, start, duration]);
    return val;
}

// ── Intersection observer hook ────────────────────────────────────────────────
function useInView(threshold = 0.2) {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const obs = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setInView(true); },
            { threshold }
        );
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, [threshold]);
    return [ref, inView];
}

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ value, label, icon, started }) {
    const count = useCountUp(value, 1800, started);
    return (
        <div style={S.statCard}>
            <div style={S.statIcon}>{icon}</div>
            <div style={S.statNumber}>{count.toLocaleString()}</div>
            <div style={S.statLabel}>{label}</div>
        </div>
    );
}

// ── Benefit section ───────────────────────────────────────────────────────────
function BenefitSection({ title, accent, items, icon }) {
    return (
        <div style={{ ...S.benefitCard, borderTopColor: accent }}>
            <div style={S.benefitHeader}>
                <span style={{ ...S.benefitIcon, color: accent }}>{icon}</span>
                <h3 style={{ ...S.benefitTitle, color: accent }}>{title}</h3>
            </div>
            <ul style={S.benefitList}>
                {items.map((item, i) => (
                    <li key={i} style={S.benefitItem}>
                        <span style={{ ...S.bulletDot, backgroundColor: accent }} />
                        <span>{item}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function Home() {
    const [stats, setStats] = useState(null);
    const [statsRef, statsInView] = useInView(0.2);

    useEffect(() => {
        axios.get(`${config.API_URL}/stats`)
            .then(r => setStats(r.data))
            .catch(() => {});
    }, []);

    const artistBenefits = [
        'Your complete show history in one place — every gig, every venue, every lineup.',
        'See who you\'ve shared the stage with via the "Played With" co-billing graph.',
        'Geographic breakdowns and year-by-year charts show how your reach has grown.',
        'Your legacy, not just your latest release — built for the full arc of a live career.',
    ];
    const fanBenefits = [
        'Browse every show your favorite artists have ever played — headlining, support, festivals.',
        'Discover new artists through the acts they\'ve toured with — the way real fans do.',
        'Go deeper than a setlist — venue history, lineup position, and performance context.',
        'Built for the obsessives. Treat live music history with the seriousness it deserves.',
    ];
    const venueBenefits = [
        'A permanent record of every artist who\'s graced your stage.',
        'Track artist trajectories — from early support slots to headline shows.',
        'Co-billing graph and show history give booking intelligence spreadsheets can\'t.',
        'Stage-level detail — multi-stage venues track exactly which stage each act played.',
    ];

    return (
        <div style={S.page}>

            {/* ── Hero ───────────────────────────────────────────────────── */}
            <section style={S.hero}>
                <div style={S.heroGlow} />
                <div style={S.heroBgGrid} />
                <div style={S.heroContent}>
                    <div style={S.heroEyebrow}>
                        <span style={S.heroEyebrowPurple}>sharc.fm</span>
                        <span style={S.heroEyebrowDivider}>|</span>
                        <span style={S.heroEyebrowSub}>Show Archives</span>
                    </div>
                    <h1 style={S.heroTitle}>
                        The Live Music<br />
                        <span style={S.heroTitleAccent}>Archive</span>
                    </h1>
                    <p style={S.missionStatement}>
                        sharc.fm aims to be the most comprehensive online catalog of live artist
                        performances, tracking the artists, producers, and venues that make it happen.
                    </p>
                    <div style={S.heroActions}>
                        <Link to="/how-it-works" style={S.heroCta}>How sharc.fm Works</Link>
                        <Link to="/my-shows-tutorial" style={S.heroCtaGhost}>How Show History Works</Link>
                        <Link to="/artists" style={S.heroCta}>Browse Artists</Link>
                        <Link to="/venues" style={S.heroCtaGhost}>Browse Venues</Link>
                    </div>
                </div>
            </section>

            {/* ── Stats ──────────────────────────────────────────────────── */}
            <section ref={statsRef} style={S.statsSection}>
                <div style={S.statsInner}>
                    <div style={S.statsGrid}>
                        <StatCard
                            value={stats?.artists || 0}
                            label="Artists"
                            icon="🎸"
                            started={statsInView && !!stats}
                        />
                        <StatCard
                            value={stats?.venues || 0}
                            label="Venues"
                            icon="🏟️"
                            started={statsInView && !!stats}
                        />
                        <StatCard
                            value={stats?.shows || 0}
                            label="Shows"
                            icon="🎤"
                            started={statsInView && !!stats}
                        />
                        <StatCard
                            value={stats?.relationships || 0}
                            label="Artist Relationships"
                            icon="🔗"
                            started={statsInView && !!stats}
                        />
                    </div>
                </div>
            </section>

            {/* ── Benefits ───────────────────────────────────────────────── */}
            <section style={S.benefitsSection}>
                <div style={S.sectionHeader}>
                    <h2 style={S.sectionTitle}>Built for Everyone in the Room</h2>
                    <p style={S.sectionSubtitle}>
                        Whether you played the show, attended it, or booked the room — SHARC has something for you.
                    </p>
                </div>
                <div style={S.benefitsGrid}>
                    <BenefitSection
                        title="For Artists"
                        accent="#bb86fc"
                        icon="🎸"
                        items={artistBenefits}
                    />
                    <BenefitSection
                        title="For Fans"
                        accent="#03dac6"
                        icon="🎟️"
                        items={fanBenefits}
                    />
                    <BenefitSection
                        title="For Venues"
                        accent="#cf6679"
                        icon="🏟️"
                        items={venueBenefits}
                    />
                </div>
            </section>

            {/* ── Differentiators strip ──────────────────────────────────── */}
            <section style={S.diffSection}>
                <div style={S.diffInner}>
                    <h2 style={S.sectionTitle}>What Sets SHARC Apart</h2>
                    <div style={S.diffGrid}>
                        {[
                            { icon: '🔗', label: '"Played With" Graph', desc: 'Map co-billing relationships across an artist\'s entire career — no other platform does this.' },
                            { icon: '📍', label: 'Venue & Stage Detail', desc: 'Multi-stage venues tracked at the stage level. Geo-mapped show history included.' },
                            { icon: '📊', label: 'Career Analytics', desc: 'Year-by-year charts, geographic breakdowns, and headline vs. support ratios.' },
                            { icon: '🎪', label: 'Festival Context', desc: 'Festivals and promoters are first-class objects, not just tags on events.' },
                        ].map((d, i) => (
                            <div key={i} style={S.diffCard}>
                                <div style={S.diffCardIcon}>{d.icon}</div>
                                <div style={S.diffCardLabel}>{d.label}</div>
                                <div style={S.diffCardDesc}>{d.desc}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

        </div>
    );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
    page: {
        backgroundColor: '#121212',
        color: '#e0e0e0',
        minHeight: '100vh',
        fontFamily: "'Segoe UI', system-ui, sans-serif",
    },

    // Hero
    hero: {
        position: 'relative',
        overflow: 'hidden',
        padding: '100px 40px 80px',
        textAlign: 'center',
        minHeight: '480px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    heroGlow: {
        position: 'absolute',
        top: '-80px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '700px',
        height: '400px',
        background: 'radial-gradient(ellipse at center, rgba(187,134,252,0.15) 0%, transparent 70%)',
        pointerEvents: 'none',
    },
    heroBgGrid: {
        position: 'absolute',
        inset: 0,
        backgroundImage: `
            linear-gradient(rgba(187,134,252,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(187,134,252,0.04) 1px, transparent 1px)
        `,
        backgroundSize: '40px 40px',
        pointerEvents: 'none',
    },
    heroContent: {
        position: 'relative',
        zIndex: 2,
        maxWidth: '700px',
        margin: '0 auto',
    },
    heroEyebrow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        marginBottom: '24px',
        fontSize: '15px',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
    },
    heroEyebrowPurple: {
        color: '#bb86fc',
        fontWeight: 700,
    },
    heroEyebrowDivider: {
        color: '#555',
        fontWeight: 300,
    },
    heroEyebrowSub: {
        color: '#888',
        fontWeight: 400,
    },
    heroTitle: {
        fontSize: 'clamp(42px, 8vw, 72px)',
        fontWeight: 800,
        lineHeight: 1.05,
        letterSpacing: '-0.02em',
        color: '#f0f0f0',
        margin: '0 0 20px',
    },
    heroTitleAccent: {
        color: '#bb86fc',
    },
    missionStatement: {
        fontSize: '17px',
        lineHeight: 1.7,
        color: '#aaa',
        maxWidth: '560px',
        margin: '0 auto 36px',
    },
    heroActions: {
        display: 'flex',
        gap: '10px',
        justifyContent: 'center',
        flexWrap: 'nowrap',
    },
    heroCta: {
        display: 'inline-block',
        padding: '11px 18px',
        backgroundColor: '#bb86fc',
        color: '#121212',
        fontWeight: 700,
        fontSize: '14px',
        borderRadius: '6px',
        textDecoration: 'none',
        letterSpacing: '0.01em',
        transition: 'opacity 0.15s',
        whiteSpace: 'nowrap',
    },
    heroCtaGhost: {
        display: 'inline-block',
        padding: '11px 18px',
        backgroundColor: 'transparent',
        color: '#bb86fc',
        fontWeight: 600,
        fontSize: '14px',
        borderRadius: '6px',
        textDecoration: 'none',
        border: '1px solid #bb86fc',
        letterSpacing: '0.01em',
        whiteSpace: 'nowrap',
    },

    // Stats
    statsSection: {
        borderTop: '1px solid #2a2a2a',
        borderBottom: '1px solid #2a2a2a',
        backgroundColor: '#1a1a1a',
        padding: '56px 40px',
    },
    statsInner: {
        maxWidth: '960px',
        margin: '0 auto',
    },
    statsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '24px',
    },
    statCard: {
        textAlign: 'center',
        padding: '32px 24px',
        backgroundColor: '#212121',
        borderRadius: '10px',
        border: '1px solid #2e2e2e',
    },
    statIcon: {
        fontSize: '28px',
        marginBottom: '12px',
    },
    statNumber: {
        fontSize: '42px',
        fontWeight: 800,
        color: '#bb86fc',
        letterSpacing: '-0.02em',
        lineHeight: 1.1,
        marginBottom: '6px',
        fontVariantNumeric: 'tabular-nums',
    },
    statLabel: {
        fontSize: '13px',
        color: '#888',
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        fontWeight: 500,
    },

    // Benefits
    benefitsSection: {
        padding: '80px 40px',
        maxWidth: '1200px',
        margin: '0 auto',
    },
    sectionHeader: {
        textAlign: 'center',
        marginBottom: '52px',
    },
    sectionTitle: {
        fontSize: 'clamp(24px, 4vw, 36px)',
        fontWeight: 700,
        color: '#f0f0f0',
        margin: '0 0 12px',
        letterSpacing: '-0.01em',
    },
    sectionSubtitle: {
        fontSize: '16px',
        color: '#888',
        margin: 0,
    },
    benefitsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '24px',
    },
    benefitCard: {
        backgroundColor: '#1e1e1e',
        borderRadius: '10px',
        border: '1px solid #2e2e2e',
        borderTop: '3px solid #bb86fc',
        padding: '28px 24px',
    },
    benefitHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        marginBottom: '20px',
    },
    benefitIcon: {
        fontSize: '22px',
    },
    benefitTitle: {
        fontSize: '18px',
        fontWeight: 700,
        margin: 0,
    },
    benefitList: {
        listStyle: 'none',
        margin: 0,
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
    },
    benefitItem: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        fontSize: '14px',
        color: '#bbb',
        lineHeight: 1.55,
    },
    bulletDot: {
        width: '6px',
        height: '6px',
        borderRadius: '50%',
        flexShrink: 0,
        marginTop: '6px',
    },

    // Differentiators
    diffSection: {
        backgroundColor: '#161616',
        borderTop: '1px solid #2a2a2a',
        padding: '80px 40px',
    },
    diffInner: {
        maxWidth: '1000px',
        margin: '0 auto',
        textAlign: 'center',
    },
    diffGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '20px',
        marginTop: '48px',
    },
    diffCard: {
        backgroundColor: '#1e1e1e',
        border: '1px solid #2e2e2e',
        borderRadius: '10px',
        padding: '28px 20px',
        textAlign: 'center',
    },
    diffCardIcon: {
        fontSize: '32px',
        marginBottom: '12px',
    },
    diffCardLabel: {
        fontSize: '15px',
        fontWeight: 700,
        color: '#e0e0e0',
        marginBottom: '8px',
    },
    diffCardDesc: {
        fontSize: '13px',
        color: '#888',
        lineHeight: 1.6,
    },
};
